import { Paper, Stack, Typography } from '@mui/material';
import { getUsagePricingModels } from '@vy/services';
import { Period, PRICING_MODELS, PricingModel } from '@vy/types';
import { isNotNullOrUndefined, raiseUnreachable } from '@vy/utils';
import { VyTooltip } from '../../../../../../components/new-design/VyTooltip';
import { formatDate, formatPeriodMonths } from '../../../../../../utils/date';
import { RenderBarProps } from './ContractPhasesDataRows';

export const ContractProductsTimelineRowBar: React.FC<RenderBarProps> = ({
  color,
  width,
  height,
  periodData,
}) => {
  const { period, title, pricingModel } = periodData;

  const isUsage = isNotNullOrUndefined(pricingModel) && getUsagePricingModels().includes(pricingModel);

  const crossedOutLinesStyle = isUsage
    ? {
        backgroundImage: `
          repeating-linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.6),
            rgba(255, 255, 255, 0.6) 1.5px,
            transparent 1.5px,
            transparent 5px
          )`,
      }
    : {};

  return (
    <div
      style={{
        width: `${width}px`,
        height: `${height}px`,
      }}
      className="flex items-start justify-center flex-col"
    >
      <VyTooltip
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: 'transparent',
            },
          },
        }}
        placement="bottom-start"
        tooltipContent={
          <Paper
            sx={{
              padding: '16px',
              width: '240px',
              height: isNotNullOrUndefined(pricingModel) ? '135px' : '115px',
            }}
          >
            <Stack direction={'column'} sx={{
              gap: 1
            }}>
              <Typography
                noWrap
                sx={{
                  textOverflow: 'ellipsis',
                  width: '100%',
                  fontSize: '15px',
                  fontWeight: 600
                }}>
                {title}
              </Typography>
              {isNotNullOrUndefined(pricingModel) && (
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 400
                  }}>
                  {`Product type: ${getPricingModelLabel(pricingModel)}`}
                </Typography>
              )}
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 400
                }}>
                {`Period: ${formatPhasePeriod(period)}`}
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 400
                }}>
                {`(${formatPeriodMonths(period)})`}
              </Typography>
            </Stack>
          </Paper>
        }
      >
        <Stack
          direction={'column'}
          sx={{
            width: '100%',
            alignItems: 'center'
          }}>
          <Stack
            direction={'row'}
            sx={{
              alignItems: 'center',
              gap: 1,
              width: '100%',
              height: '100%'
            }}>
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 600
              }}>
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 400
              }}>
              {formatPhasePeriod(period)}
            </Typography>
          </Stack>
          <div
            style={{
              height: '20px',
              width: '100%',
              backgroundColor: color,
              ...crossedOutLinesStyle,
            }}
          ></div>
        </Stack>
      </VyTooltip>
    </div>
  );
};

function getPricingModelLabel(pricingModel: PricingModel) {
  switch (pricingModel) {
    case PRICING_MODELS.PerUnit:
      return 'Per Unit';
    case PRICING_MODELS.Tiers:
      return 'Tiered';
    case PRICING_MODELS.TieredSubscription:
      return 'Tiered Subscription';
    case PRICING_MODELS.CommercialTerms:
      return 'Contract Terms';
    case PRICING_MODELS.Commission:
      return 'Commission';
    case PRICING_MODELS.FixedDeduction:
      return 'Discount';
    case PRICING_MODELS.FixedOneTime:
      return 'One-Time Fee';
    case PRICING_MODELS.PercentageDeduction:
      return 'Percentage Discount';
    case PRICING_MODELS.Refund:
      return 'Refund';
    case PRICING_MODELS.Subscription:
      return 'Subscription';
    default:
      return raiseUnreachable(pricingModel);
  }
}

function formatPhasePeriod(period: Period) {
  const startString = formatDate(period.startTime, 'MMM DD, YY');
  const endString = formatDate(period.endTime, 'MMM DD, YY');

  return `${startString} - ${endString}`;
}
